import React, { useState, useEffect } from 'react';
import { Collapsible, CollapsibleContent } from '../ui/collapsible';
import { ChevronDown, Info } from 'lucide-react';
import { PriceDetail } from '../list/flightlist/types';
import { cn, formatCurrency } from '../../lib/utils';
import { useTranslations } from '@/lib/i18n';
import { useSelector } from 'react-redux';
import { getPassengerBreakdown } from '../../lib/passenger-amounts';

// Define a simple Skeleton component
const Skeleton = ({ width, height }: { width: string; height: string }) => (
  <div
    className={`skeleton-loader ${width} ${height} flex items-center justify-center rounded-md`}
  >
    <span className="flex gap-1">
      <span className="dot h-2 w-2 animate-bounce rounded-full bg-current"></span>
      <span className="dot h-2 w-2 animate-bounce rounded-full bg-current delay-200"></span>
      <span className="dot h-2 w-2 animate-bounce rounded-full bg-current delay-400"></span>
    </span>
  </div>
);

const FareSummary = ({
  summary,
  callback,
  addOns,
  children,
  className,
  isLoader,
  convenienceFee,
}: {
  summary?: PriceDetail;
  callback?: () => void;
  addOns?: { isShow: boolean };
  children?: React.ReactNode;
  className?: string;
  isLoader?: boolean;
  convenienceFee?: { isShow: boolean, fee: number };
}) => {
  const b = useTranslations('buttons');
  const [isBaseFareOpen, setBaseFareOpen] = useState(false);
  const [isConvenienceFeeOpen, setConvenienceFeeOpen] = useState(false);
  const [isTaxesOpen, setTaxesOpen] = useState(false);
  const [isAddOnsOpen, setAddOnsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const flightSearch = useSelector((state: any) => state.flightSearch);

  const [fareData, setFareData] = useState<any>(flightSearch?.fareDetails);
  const [passengerBreakdown, setPassengerBreakdown] = useState<any[]>([]);
  const [isPassengerBreakdownOpen, setPassengerBreakdownOpen] = useState(false);

  useEffect(() => {
    if (flightSearch.selectedSSR) {
      const totalSeat = flightSearch.selectedSSR.reduce(
        (total: number, pax: any) => {
          return (
            total +
            pax.reduce((flightTotal: number, flight: any) => {
              return (
                flightTotal +
                flight.reduce((segmentTotal: number, segment: any) => {
                  return segmentTotal + (segment.seat?.amount || 0);
                }, 0)
              );
            }, 0)
          );
        },
        0,
      );

      const totalMeal = flightSearch.selectedSSR.reduce(
        (total: number, pax: any) => {
          return (
            total +
            pax.reduce((flightTotal: number, flight: any) => {
              return (
                flightTotal +
                flight.reduce((segmentTotal: number, segment: any) => {
                  return (
                    segmentTotal +
                    (segment.meal?.amount || 0) * (segment.meal?.count || 0)
                  );
                }, 0)
              );
            }, 0)
          );
        },
        0,
      );

      const totalBaggage = flightSearch.selectedSSR.reduce(
        (total: number, pax: any) => {
          return (
            total +
            pax.reduce((flightTotal: number, flight: any) => {
              // Only consider the first leg of the flight
              const firstSegment = flight[0];
              return (
                flightTotal +
                (firstSegment?.baggage?.amount || 0) *
                  (firstSegment?.baggage?.count || 0)
              );
            }, 0)
          );
        },
        0,
      );
      setFareData({
        ...flightSearch.fareDetails,
        seat: totalSeat,
        meal: totalMeal,
        baggage: totalBaggage,
        addOns: totalSeat + totalMeal + totalBaggage,
      });

      // Calculate passenger-wise breakdown
      if (flightSearch.passenger && flightSearch.selectedSSR) {
        const breakdown = getPassengerBreakdown(
          flightSearch.selectedSSR,
          flightSearch.passenger,
        );
        setPassengerBreakdown(breakdown);
      }
    }
  }, [flightSearch]);


  const handleClick = async () => {
    if (callback) {
      setIsLoading(true);
      try {
        await callback();
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className={cn("rounded-lg bg-white p-6 shadow-lg", className)}>
      <h2 className="mb-4 text-base font-medium text-zinc-800">Fare Summary</h2>

      {/* Base Fare Section */}
      <Collapsible open={isBaseFareOpen} onOpenChange={setBaseFareOpen}>
        <div
          className="flex cursor-pointer items-center justify-between text-sm"
          onClick={() => setBaseFareOpen(!isBaseFareOpen)}
        >
          <span className="flex items-center text-xs">Base Fare</span>
          <span className="text-zinc-800">
            {isLoader ? (
              <Skeleton width="w-16" height="h-4" />
            ) : (
              formatCurrency(Number(fareData?.base_fare) || 0)
            )}
          </span>
        </div>
      </Collapsible>

      {/* Taxes & Fees Section */}
      <Collapsible
        open={isTaxesOpen}
        onOpenChange={setTaxesOpen}
        className="mt-4"
      >
        <div
          className="flex cursor-pointer items-center justify-between text-sm"
          onClick={() => setTaxesOpen(!isTaxesOpen)}
        >
          <span className="flex items-center text-xs">
            Taxes & Fees
          </span>
          <span className="text-zinc-800">
            {isLoader ? (
              <Skeleton width="w-16" height="h-4" />
            ) : (
              formatCurrency(
                  Number(fareData?.management_fee || 0) +
                  Number(fareData?.management_fee_tax || 0) +
                  Number(fareData?.airline_gst || 0) +
                  Number(fareData?.YQ || 0) +
                  Number(fareData?.YR || 0) +
                  Number(fareData?.otherTax || 0) + Number(fareData?.commissionTds || 0) - Number(fareData?.commission || 0),
              ) || '₹ 0'
            )}
          </span>
        </div>
      </Collapsible>

      {/* Passenger-wise Breakdown Section */}
      {addOns?.isShow && passengerBreakdown.length > 0 && (
        <Collapsible
          open={isPassengerBreakdownOpen}
          onOpenChange={setPassengerBreakdownOpen}
          className="mt-4"
        >
          <div
            className="flex cursor-pointer items-center justify-between text-sm"
            onClick={() => setPassengerBreakdownOpen(!isPassengerBreakdownOpen)}
          >
            <span className="flex items-center text-xs">
              Passenger-wise Breakdown
              <ChevronDown className="h-4 w-4 shrink-0 text-primary transition-transform duration-200" />
            </span>
            <span className="text-zinc-800">
              {isLoader ? (
                <Skeleton width="w-16" height="h-4" />
              ) : (
                formatCurrency(
                  passengerBreakdown.reduce(
                    (total, pax) => total + pax.amounts.total,
                    0,
                  ),
                ) || '₹ 0'
              )}
            </span>
          </div>
          <CollapsibleContent className="text-sm">
            {passengerBreakdown.map((passenger, index) => (
              <div
                key={index}
                className="border-b border-gray-100 py-2 last:border-b-0"
              >
                <div className="flex items-center justify-between py-1">
                  <p className="text-xs font-medium text-gray-700">
                    {passenger.name}
                  </p>
                  <p className="text-xs font-semibold">
                    {formatCurrency(passenger.amounts.total)}
                  </p>
                </div>
                <div className="ml-2 space-y-1">
                  {passenger.amounts.seat > 0 && (
                    <div className="flex items-center justify-between text-xs text-gray-600">
                      <span>Seat</span>
                      <span>{formatCurrency(passenger.amounts.seat)}</span>
                    </div>
                  )}
                  {passenger.amounts.meal > 0 && (
                    <div className="flex items-center justify-between text-xs text-gray-600">
                      <span>Meal</span>
                      <span>{formatCurrency(passenger.amounts.meal)}</span>
                    </div>
                  )}
                  {passenger.amounts.baggage > 0 && (
                    <div className="flex items-center justify-between text-xs text-gray-600">
                      <span>Baggage</span>
                      <span>{formatCurrency(passenger.amounts.baggage)}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Convenience Fee Section */}
      {convenienceFee?.isShow && (
        <Collapsible
          open={isConvenienceFeeOpen}
          onOpenChange={setConvenienceFeeOpen}
          className="mt-4"
        >
          <div
            className="flex cursor-pointer items-center justify-between text-sm"
            onClick={() => setConvenienceFeeOpen(!isConvenienceFeeOpen)}
          >
            <span className="flex items-center text-xs">Convenience Fee</span>
            <span className="text-zinc-800">{formatCurrency(convenienceFee?.fee)}</span>
          </div>
        </Collapsible>
      )}

      {/* Total Amount */}
      <div className="border-light mb-4 flex items-start justify-between border-b border-t py-4">
        <div className="text-base font-medium text-zinc-800">
          Total Amount
          <div className="flex text-[10px] text-text_secondary">
            *Convenience fee may apply
            <div className="group relative cursor-pointer text-sm">
              <Info className="absolute -right-3 h-2 w-2" />

              <div className="absolute -right-12 mt-2 hidden w-48 rounded bg-black p-2 text-[10px] font-normal text-white group-hover:block sm:right-0">
                Convenience fee may apply on all Domestic & International
                sectors for all online payment modes.
              </div>
            </div>
          </div>
        </div>
        <span className="text-base font-medium text-zinc-800">
          {isLoader ? (
            <Skeleton width="w-16" height="h-4" />
          ) : convenienceFee?.isShow ? (
            formatCurrency(
              Number(fareData?.netfare || 0)  +
                Number(
                  fareData?.seat + fareData?.meal + fareData?.baggage || 0,
                ),
            )
          ) : (
            formatCurrency(
              Number(fareData?.netfare || 0)  +
                Number(
                  fareData?.seat + fareData?.meal + fareData?.baggage || 0,
                ),
            )
          )}
        </span>
      </div>
      <button
        onClick={handleClick}
        disabled={isLoading || isLoader}
        className="w-full rounded-lg bg-primary py-2 font-semibold text-white transition duration-300 hover:bg-primary disabled:opacity-50"
      >
        {isLoading || isLoader ? (
          <span className="flex items-center justify-center gap-2">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
            Loading...
          </span>
        ) : (
          children || b('continue')
        )}
      </button>
    </div>
  );
};

export default FareSummary;
